import { AdyenCheckout, PaymentAction } from "@adyen/adyen-web"
import React, { useEffect, useRef } from "react"
import { useI18next } from "gatsby-plugin-react-i18next"
import { getAdyenEnvironment, getAdyenLocale } from "../../../util/util"
import { useAPI } from "../../../services/api"
import { captureException } from "../../../services/exception-logger"

type Props = {
  action: PaymentAction | undefined
  countryCode: string
  payment_details_url: string
  client_key: string
  showPaymentFailedScreen: (
    errorMessage?: string,
    providerName?: string
  ) => void
}

export default function AdyenCardSCA({
  action,
  payment_details_url,
  countryCode,
  client_key,
  showPaymentFailedScreen,
}: Props) {
  const containerRef = useRef(null)
  const api = useAPI()
  const { language } = useI18next()

  useEffect(() => {
    if (!action) {
      return
    }

    const initializeCheckout = async () => {
      const checkout = await AdyenCheckout({
        clientKey: client_key,
        locale: getAdyenLocale(language),
        countryCode,
        environment: getAdyenEnvironment(client_key),

        // This is called after 3DS2 challenge or redirect returns info
        onAdditionalDetails: async (state, components, actions) => {
          const data = state.data

          try {
            const details = await api.payments.postAdyenPaymentDetails({
              data,
              paymentDetailsUrl: payment_details_url,
            })
            actions.resolve({
              resultCode: details.resultCode,
            })
          } catch (error) {
            captureException(error, {
              type: "network",
              target: "internal-api",
              category: "payment",
              step: "sca-challenge",
              provider: "adyen",
              context: { errorSource: "AdyenCardSCA - onAdditionalDetails" },
            })
            actions.reject()
          }
        },

        // Optional error callback
        onError: (error) => {
          captureException(error, {
            type: "network",
            target: "external-api",
            category: "payment",
            step: "sca-challenge",
            provider: "adyen",
            context: { errorSource: "AdyenCardSCA - onError" },
          })
          showPaymentFailedScreen(error.message, "Adyen Card SCA")
        },
      })

      checkout.createFromAction(action).mount(containerRef.current!)
    }

    initializeCheckout()
  }, [action, payment_details_url])

  return <div ref={containerRef} />
}
